import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import { API_BASE_URL } from "../common/config";

/**
 * Live email preview (rendered by the server) plus a "send test to me" action.
 * @param {Object} request - body for /api/mailings/preview and /api/mailings/test (without language)
 * @param {boolean} active - only refresh the preview while the tab is visible
 */
const MailingPreview = ({ request, language, active = true }) => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [darkPreview, setDarkPreview] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const requestId = useRef(0);
  const frameRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);

  const body = JSON.stringify({ ...request, language });

  // Re-render the preview shortly after the user stops typing
  useEffect(() => {
    if (!active) return undefined;
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      const res = await updateDb('/api/mailings/preview', JSON.parse(body));
      if (id !== requestId.current) return;
      setLoading(false);
      if (res.success) {
        setPreview(res.data);
        setError(null);
      } else {
        setError(res.error || t('mailing.error'));
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [body, active]);

  useEffect(() => setStatus(null), [body]);

  const sendTest = async () => {
    setTesting(true);
    setStatus(null);
    const res = await updateDb('/api/mailings/test', JSON.parse(body));
    setTesting(false);
    setStatus(res.success
      ? { type: 'success', text: t('mailing.test_sent', { email: res.data.email }) }
      : { type: 'error', text: res.error || t('mailing.error') });
  };

  // Force the email's dark-mode styles on or off, regardless of the OS setting
  const previewDocument = preview
    ? preview.html
      .replace('<head>', '<head><base target="_blank">')
      .replace(/@media \(prefers-color-scheme: dark\)/g, darkPreview ? '@media all' : '@media not all')
    : '';

  // The frame announces itself once loaded; from then on we push every new render into it
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data === 'mail-preview-ready' && e.source === frameRef.current?.contentWindow) setFrameReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (frameReady && previewDocument) frameRef.current?.contentWindow?.postMessage(previewDocument, '*');
  }, [frameReady, previewDocument]);

  return (
    <div className="mail-preview">
      <div className="mail-preview-head">
        <span className="mail-preview-title"><i className="icon-view" /> {t('mailing.preview')}</span>
        <button type="button" className={`mail-icon-btn ${darkPreview ? 'active' : ''}`} aria-pressed={darkPreview} onClick={() => setDarkPreview(!darkPreview)}>
          {t('mailing.preview_dark')}
        </button>
        <button type="button" className="mail-icon-btn" disabled={testing} onClick={sendTest} title={t('mailing.send_test', { email: currentUser?.email })}>
          <i className="icon-mail" /> {testing ? t('mailing.sending') : t('mailing.send_test_short')}
        </button>
      </div>
      {status && (
        <div className={`mail-status ${status.type}`} role="status" style={{ margin: 12 }}>
          <i className={status.type === 'success' ? 'icon-check-circle' : 'icon-alert'} /> {status.text}
        </div>
      )}
      <div className="mail-preview-subject">
        <span>{t('mailing.field_subject')}:</span> <strong>{preview?.subject || '…'}</strong>
      </div>
      <div className={`mail-preview-frame ${loading ? 'loading' : ''}`}>
        {/* Sandboxed frame with its own CSP (see /api/mailings/preview-frame); links open in a new tab */}
        <iframe
          ref={frameRef}
          title={t('mailing.preview')}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          src={`${API_BASE_URL}/api/mailings/preview-frame`}
          hidden={!preview}
        />
        {!preview && <div className="mail-preview-msg">{error || t('mailing.preview_loading')}</div>}
      </div>
      {preview && error && <div className="mail-status error" style={{ margin: 12 }}><i className="icon-alert" /> {error}</div>}
      <div className="mail-preview-foot">{t('mailing.preview_note')} {t('mailing.test_help')}</div>
    </div>
  );
};

export default MailingPreview;
